import {Component, DestroyRef, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren} from '@angular/core';
import {FormerTenureValidity} from '../../../common/models/former-tenure-validity.model';
import {ParentPlanType} from '../../../common/models/parent-plan-type.model';
import {FormerTenureUiConfig} from '../../../common/models/former-tenure-ui-config';

import {Tenure} from '../../../common/models/tenure-model';
import {NgEventBus} from 'ng-event-bus';
import {NlrHasPermissionsPipe} from '../../../common/pipe/nlr-has-permissions.pipe';
import {NlrPermissionsDisabledPipe} from '../../../common/pipe/nlr-permissions-disabled-pipe';
import {ControlContainer, FormsModule, NgForm, NgModel, ReactiveFormsModule} from '@angular/forms';
import {NgSelectModule} from '@ng-select/ng-select';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-tenure-parent-plan-type',
    imports: [NlrHasPermissionsPipe, NlrPermissionsDisabledPipe, NgSelectModule, ReactiveFormsModule, FormsModule],
    templateUrl: './tenure-parent-plan-type.component.html',
    styleUrl: './tenure-parent-plan-type.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class TenureParentPlanTypeComponent implements OnInit, OnChanges {
    parentPlanTypeOptions = [];
    @Input() name: string;
    @Input() tenure: Tenure;
    @Input() formerTenureValidityLookup: FormerTenureValidity;
    @Input() parentPlanTypeLookups: ParentPlanType[];
    @Input() formerTenureUiConfigLookup: FormerTenureUiConfig;
    @Input() label: string;
    @Input() tenureDisplayType: string;
    @Input() readonly: boolean;
    parentPlanType: any;

    constructor(
        private eventBus: NgEventBus,
        private destroyRef: DestroyRef
    ) {}

    @ViewChildren(NgModel) inputModels!: QueryList<NgModel>;

    markAllInputsAsUntouched() {
        this.inputModels.forEach(model => {
            model.control.markAsUntouched();
            model.control.markAsPristine();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['tenure']) {
            // Handles NLOP - No Lot on Plan Identifier
            // NLOP is a special parentPlanType that has no ID. It also doesn't exist in the provided parentPlanTypeLookups (for good reason).
            // In order to handle displaying of existing NLOP data correctly, we need to check if we have an existing tenure with a null
            // parentPlanType. If we do, we explicitly set the parentPlanType to match NLOP (null) so it displays as expected
            if (changes['tenure'].currentValue.parentPlanType === null && changes['tenure'].currentValue) {
                if (this.formerTenureValidityLookup?.subjectLandTypes) {
                    this.parentPlanType = {
                        id: null,
                        name: this.formerTenureUiConfigLookup.parentPlanTypeSubjectLandOption
                    };
                }
            } else {
                const planTypeCode = this.tenure.parentPlanType ?? this.formerTenureValidityLookup.defaultPlanType;
                this.parentPlanType = this.parentPlanTypeLookups.filter(p => p.id === planTypeCode)[0];
            }
        }
    }

    ngOnInit(): void {
        this.eventBus
            .on('add-new-tenure')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.markAllInputsAsUntouched();
            });

        this.parentPlanTypeLookups.forEach(parentPlanType => {
            this.parentPlanTypeOptions.push({id: parentPlanType.id, name: parentPlanType.name});
        });

        if (this.formerTenureValidityLookup?.subjectLandTypes) {
            this.parentPlanTypeOptions.push({
                id: null,
                name: this.formerTenureUiConfigLookup.parentPlanTypeSubjectLandOption
            });
        }
    }

    onSelectParentPlanType(option: ParentPlanType) {
        this.tenure.parentPlanType = option.id;
        this.parentPlanType = option;
        if (this.tenure.parentPlanType) {
            this.tenure.lots = [
                {
                    parentLotIdentifier: '',
                    newLotDetails: {
                        fullySupersededByRoad: false,
                        newLotRanges: []
                    },
                    nonLotCommonProperty: false
                }
            ];
            if (this.tenure.title) {
                delete this.tenure.title;
            }
            if (this.tenure.subjectLandDescription) {
                delete this.tenure.subjectLandDescription;
            }
        } else {
            this.tenure.lots = [];
            if (this.tenureDisplayType === 'FORMER_TENURE_PORTAL' || this.tenureDisplayType === 'FORMER_TENURE_DIGITAL') {
                this.tenure.newLotDetails = this.tenure.newLotDetails || {newLotRanges: [], fullySupersededByRoad: false};
                this.tenure.newLotDetails.newLotRanges = this.tenure.newLotDetails.newLotRanges || [];
                this.tenure.newLotDetails.newLotRanges.length = 0;
            }
            delete this.tenure.parentPlan;
        }
    }
}
